import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime.js';
import utc from 'dayjs/plugin/utc.js';
import timezone from 'dayjs/plugin/timezone.js';

dayjs.extend(relativeTime);
dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * The company's timezone, which is what every date and time here is shown in.
 *
 * Attendance is defined by the office clock, not by wherever the browser or
 * the server happens to be. Without this, a console opened from a machine in
 * one zone disagreed with a server running in another: a punch stored at
 * 10:36Z rendered as 10:36 for a UTC browser and 16:06 for an IST one, and a
 * manual edit then wrote the naive value back in the *server's* zone, so a
 * round trip through the edit dialog silently shifted the time.
 */
export const COMPANY_TZ = import.meta.env.VITE_COMPANY_TZ ?? 'Asia/Kolkata';

export { dayjs };

/** An instant, re-expressed in company time. */
export const inCompanyTime = (value) => dayjs(value).tz(COMPANY_TZ);

/**
 * Fills a `datetime-local` input from a stored instant.
 *
 * The input has no concept of a zone, so it must be handed the company-time
 * wall clock; anything else shows the user a different time than the record
 * holds.
 */
export function toLocalInput(value) {
  return value ? inCompanyTime(value).format('YYYY-MM-DDTHH:mm') : '';
}

/**
 * Reads a `datetime-local` back, as a real instant.
 *
 * The browser gives a naive "2026-11-01T10:36". Sending that unqualified
 * leaves the server to guess a zone, and it guesses its own. Anchoring it to
 * company time here makes what the user typed mean what they meant.
 */
export function fromLocalInput(value) {
  return value ? dayjs.tz(value, COMPANY_TZ).toISOString() : null;
}

/** ₹1,23,456.78 — Indian digit grouping. */
export function inr(value, { decimals = 2, symbol = true } = {}) {
  const num = Number(value) || 0;
  const negative = num < 0;
  const [whole, frac] = Math.abs(num).toFixed(decimals).split('.');

  const grouped =
    whole.length <= 3
      ? whole
      : `${whole.slice(0, -3).replace(/\B(?=(\d{2})+(?!\d))/g, ',')},${whole.slice(-3)}`;

  return `${negative ? '-' : ''}${symbol ? '₹' : ''}${decimals > 0 ? `${grouped}.${frac}` : grouped}`;
}

/** Compact figure for dashboard tiles: 1234567 -> "₹12.35L". */
export function inrCompact(value) {
  const num = Number(value) || 0;
  const abs = Math.abs(num);
  const sign = num < 0 ? '-' : '';

  if (abs >= 10_000_000) return `${sign}₹${(abs / 10_000_000).toFixed(2)}Cr`;
  if (abs >= 100_000) return `${sign}₹${(abs / 100_000).toFixed(2)}L`;
  if (abs >= 1000) return `${sign}₹${(abs / 1000).toFixed(1)}K`;
  return inr(num, { decimals: 0 });
}

export const fmt = {
  date: (value) => (value ? inCompanyTime(value).format('DD MMM YYYY') : '—'),
  dateLong: (value) => (value ? inCompanyTime(value).format('dddd, D MMMM YYYY') : '—'),
  dateTime: (value) => (value ? inCompanyTime(value).format('DD MMM YYYY, h:mm A') : '—'),
  time: (value) => (value ? inCompanyTime(value).format('h:mm A') : '—'),
  month: (period) => (period ? dayjs(`${period}-01`).format('MMMM YYYY') : '—'),
  monthShort: (period) => (period ? dayjs(`${period}-01`).format('MMM YY') : '—'),
  fromNow: (value) => (value ? dayjs(value).fromNow() : '—'),

  /** 506 -> "8h 26m" */
  duration: (minutes) => {
    if (minutes == null) return '—';
    const safe = Math.max(0, Math.round(minutes));
    return `${Math.floor(safe / 60)}h ${String(safe % 60).padStart(2, '0')}m`;
  },

  /** 2.5 -> "2.5 days", 1 -> "1 day" */
  days: (value) => {
    const num = Number(value) || 0;
    return `${num % 1 === 0 ? num : num.toFixed(1)} ${num === 1 ? 'day' : 'days'}`;
  },

  percent: (value, decimals = 1) => `${(Number(value) || 0).toFixed(decimals)}%`,

  bytes: (value) => {
    const num = Number(value) || 0;
    if (num < 1024) return `${num} B`;
    if (num < 1024 * 1024) return `${(num / 1024).toFixed(1)} KB`;
    return `${(num / 1024 / 1024).toFixed(1)} MB`;
  },

  /** ENUM_VALUE -> "Enum value" */
  label: (value) =>
    String(value ?? '')
      .replace(/_/g, ' ')
      .toLowerCase()
      .replace(/^./, (c) => c.toUpperCase()),

  initials: (name) =>
    String(name ?? '')
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase())
      .join('') || '?',
};

/** Current `YYYY-MM`, and the N months before it — for period pickers. */
export function recentPeriods(count = 12, from = new Date()) {
  return Array.from({ length: count }, (_, i) => {
    const d = dayjs(from).subtract(i, 'month');
    return { value: d.format('YYYY-MM'), label: d.format('MMMM YYYY') };
  });
}

/** Indian financial year label for a date: April–March. */
export function financialYear(value = new Date()) {
  const d = dayjs(value);
  const startYear = d.month() >= 3 ? d.year() : d.year() - 1;
  return `${startYear}-${String((startYear + 1) % 100).padStart(2, '0')}`;
}

export function recentFinancialYears(count = 5) {
  return Array.from({ length: count }, (_, i) => {
    const year = dayjs().subtract(i, 'year');
    return financialYear(year);
  });
}

/** Colour token per status, so badges look the same everywhere. */
export const STATUS_TONE = {
  PRESENT: 'success', IN_PROGRESS: 'info', ABSENT: 'danger', HALF_DAY: 'warning',
  WEEK_OFF: 'neutral', HOLIDAY: 'purple', ON_LEAVE: 'info', MISPUNCH: 'warning',

  PENDING: 'warning', APPROVED: 'success', REJECTED: 'danger', CANCELLED: 'neutral',

  DRAFT: 'neutral', PROCESSING: 'info', PROCESSED: 'info',
  PAID: 'success', PUBLISHED: 'success', ON_HOLD: 'warning',

  ACTIVE: 'success', PROBATION: 'info', NOTICE: 'warning',
  RESIGNED: 'neutral', TERMINATED: 'danger',
};
